// @ts-nocheck — frozen design snapshot, intentionally not type-checked
/**
 * AMZEL Nest — Site Detail v2-13 (Design Preview, iteriert nach v2-12-Feedback)
 *
 * Public route: /preview/sites/:code/v2-13  (Daniel-requested)
 *
 * Änderungen gegenüber v2-12:
 *  • Übersicht-KPI-Tiles sind jetzt klickbar — springen direkt zum
 *    passenden Tab (Funkgeräte/Akku/Repeater/Ladestationen → Devices,
 *    In Reparatur → RMA, Frequenz-Lizenz → Frequenzen,
 *    Verschlüsselung → Sicherheit, Antennen → neuer Tab Funkzelle).
 *  • Neuer Tab "Funkzelle": Antennen + Antennen-Kabel (Typ, Marke, Meter)
 *    + Splitter + Taps + Koppelnetzwerk + Heatmaps.
 *  • Infrastruktur-Tab entfernt — Inhalte komplett auf Devices+Funkzelle
 *    verteilt.
 *  • Repeater-Card in Devices zeigt jetzt die volle Konfigurations-Tabelle
 *    (Master + 3 Peers: Model, SN, FW, License, IP, UDP, Radio-ID, Rx/Tx,
 *    System Code, Slot Channel IDs) — analog As-Built-PDF.
 *
 * v2 / v2-2 … v2-12 bleiben Snapshots.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  Antenna,
  ArrowRight,
  Battery,
  BatteryCharging,
  CalendarClock,
  Camera,
  CheckCircle,
  CheckCircle2,
  CircleDot,
  Download,
  Eye,
  FileArchive,
  FileText,
  KeyRound,
  Lock,
  Mail,
  MapPin,
  MessageSquare,
  PackageOpen,
  QrCode,
  Radio,
  RadioTower,
  Search,
  ShieldCheck,
  Smartphone,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { PAD2 } from "../data/pad2";
import { ShellV2 } from "./shell-v2";

// ---------------------------------------------------------------------------
// PAD2-Mock-Erweiterungen (Stand 21.05.2026)
// ---------------------------------------------------------------------------

const PAD2_DEVICES = [
  { model: "Motorola R7", icon: Radio, total: 107, in_service: 95, in_rma: 3, missing: 1 },
  { model: "Motorola DP4800e", icon: Smartphone, total: 202, in_service: 180, in_rma: 7, missing: 0 },
  { model: "Motorola DM4600 (Mobil)", icon: Radio, total: 8, in_service: 8, in_rma: 0, missing: 0 },
] as const;

const PAD2_BATTERIES = {
  total: 612, avg_health_pct: 87, health_min: 62, health_max: 99,
  chargers: 24, charger_model: "IMPRES NNTN8895 (6-Bay)",
} as const;

const PAD2_RMA = [
  { rma_id: "RMA-2026-0428", model: "Motorola DP4800e", serial: "478JKL0042", radio_id: "1042",
    issue: "Display defekt", sent_at: "2026-05-08", eta: "2026-05-28", vendor: "Spreenauten — Berlin (HQ)" },
  { rma_id: "RMA-2026-0431", model: "Motorola R7", serial: "478MNO1234", radio_id: "1108",
    issue: "Akku lädt nicht mehr", sent_at: "2026-05-12", eta: "2026-06-05", vendor: "Spreenauten — Stettin (PL)" },
  { rma_id: "RMA-2026-0435", model: "Motorola DP4800e", serial: "478JKL0078", radio_id: "1078",
    issue: "PTT-Taste klemmt", sent_at: "2026-05-14", eta: "2026-05-30", vendor: "Spreenauten — Berlin (HQ)" },
  { rma_id: "RMA-2026-0440", model: "Motorola R7", serial: "478MNO5689", radio_id: "1145",
    issue: "Wasserschaden, Mikrofon defekt", sent_at: "2026-05-17", eta: "2026-06-12", vendor: "Spreenauten — Paris (FR)" },
] as const;

const SPREENAUTEN_WORKSHOPS = [
  "Spreenauten — Berlin (HQ)", "Spreenauten — Stettin (PL)",
  "Spreenauten — Paris (FR)", "Spreenauten — Vigo (ES)",
] as const;

const PAD2_REPEATER_LOCATIONS: Record<string, string> = {
  "PAD2-REP-01": "MDF PAD2 · IT-Raum",
  "PAD2-REP-02": "MDF PAD2 · IT-Raum",
  "PAD2-REP-03": "MDF PAD2 · IT-Raum",
  "PAD2-REP-04": "MDF PAD2 · IT-Raum",
};

const PAD2_CPS = {
  current: {
    version: "2.146.122.0", released_at: "2026-04-02",
    filename: "Motorola_CPS_2.146.122.0_setup.exe", size_mb: 412,
    platform: "Windows 10 / 11 (64-bit)",
    firmware_range: "ab R20.00.00",
    release_notes_url: "https://learning.motorolasolutions.com/cps-release-notes",
  },
  // Legacy-Versionen — CPS ist nicht abwärtskompatibel. Funkgeräte mit
  // älterer Firmware brauchen die jeweils passende ältere CPS.
  legacy: [
    {
      version: "2.110.0.5", released_at: "2024-09-12",
      filename: "Motorola_CPS_2.110.0.5_setup.exe", size_mb: 385,
      firmware_range: "R19.00 – R19.50",
      note: "Für DP4800e mit Firmware R19.x (Bestand vor 2025)",
    },
    {
      version: "2.40.5.0", released_at: "2020-06-08",
      filename: "Motorola_CPS_2.40.5.0_setup.exe", size_mb: 312,
      firmware_range: "R02.10 – R02.20",
      note: "Für ältere DP4800 + DM4600 vor Firmware-Upgrade 2021",
    },
    {
      version: "1.20.5.0", released_at: "2017-03-22",
      filename: "Motorola_CPS_1.20.5.0_setup.exe", size_mb: 244,
      firmware_range: "R01.05 – R01.10",
      note: "Für DR3000-Repeater und sehr alte Handhelds (vor 2018)",
    },
  ],
} as const;

const PAD2_CODEPLUGS_BY_MODEL = [
  { model: "Motorola SLR5500", subtitle: "Repeater (Master + 3 Peers)",
    versions: [{ version: "v1.4.2", department: "Site-Standard", deployed_at: "2026-05-14",
      note: "Capacity Plus Single Site, alle 4 Repeater synchronisiert" }] },
  { model: "Motorola R7", subtitle: "Handheld · 95 Geräte im Field-Use",
    versions: [
      { version: "v1.4.2", department: "Site-Standard", deployed_at: "2026-05-14", note: "Standard für alle Field-User" },
      { version: "v1.2.0-OPS", department: "Operations", deployed_at: "2026-04-22", note: "Eigene Talkgroups + Notruf-Override" },
      { version: "v1.1.5-IT", department: "IT / Onsite-Tech", deployed_at: "2026-03-10", note: "Eigene Talkgroup + 2 Scan-Listen" },
    ] },
  { model: "Motorola DP4800e", subtitle: "Handheld · 180 Geräte im Field-Use",
    versions: [
      { version: "v3.2.1", department: "Site-Standard", deployed_at: "2026-05-14", note: "Standard-Codeplug" },
      { version: "v3.1.0-OPS", department: "Operations", deployed_at: "2026-04-22", note: "Loss-Prevention-TG aktiviert" },
    ] },
  { model: "Motorola DM4600", subtitle: "Mobil-Funkgerät · 8 Geräte (Stapler + Pforte)",
    versions: [{ version: "v2.0.3", department: "Site-Standard", deployed_at: "2026-04-22",
      note: "Mobil-Codeplug für Stapler-Fahrer & Pforte" }] },
] as const;

const PAD2_SECRETS = [
  { label: "AES-256 Verschlüsselungs-Key", sub: "Talkgroup TS1 + TS2", kind: "funk",
    last_rotated: "2026-05-14", rotation_due: "2026-08-12" },
  { label: "SLR5500 Master Repeater · CPS-Programmier-Passwort", sub: "Motorola CPS", kind: "repeater",
    last_rotated: "2026-03-15", rotation_due: "2026-06-13" },
  { label: "SLR5500 Web-Admin Login", sub: "Alle 4 Repeater", kind: "repeater",
    last_rotated: "2026-02-01", rotation_due: "2026-05-02" },
  { label: "SLR5500 Telnet:8501 Reset-Passwort", sub: "Notfall-Reset über Console", kind: "repeater",
    last_rotated: "2025-12-10", rotation_due: "2026-03-10" },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
}
function daysUntil(iso: string): number {
  return Math.round((new Date(iso + "T00:00:00").getTime() - new Date().setHours(0, 0, 0, 0)) / 86400000);
}
function fmtKb(kb: number): string {
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
}

const SITE_TYPE_LABELS: Record<string, string> = {
  FC: "Fulfillment Center", AMZL: "Last-Mile-Standort", AMXL: "Heavy & Bulky",
  ATS: "Transportstandort", VENDORFLEX: "Vendor Flex", SORTCENTER: "Sort Center", OTHER: "Sonstige",
};
const ACTIVITY_LABELS: Record<string, string> = {
  install: "Installation", maintenance: "Wartung", survey: "Coverage-Survey", audit: "Audit", repair: "Reparatur",
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

type TabId = "overview" | "devices" | "cell" | "freq" | "codeplugs" | "security" | "rma" | "techdebt" | "docs";

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: "overview", label: "Übersicht" },
  { id: "devices", label: "Devices" },
  { id: "cell", label: "Funkzelle" },
  { id: "freq", label: "Frequenzen" },
  { id: "codeplugs", label: "Codeplugs" },
  { id: "security", label: "Sicherheit" },
  { id: "rma", label: "RMA" },
  { id: "techdebt", label: "Tech Debt" },
  { id: "docs", label: "Dokumente & Crew" },
];

/** Antennen-Netzwerk-Komponenten (Kabel, Splitter, Taps, Koppelnetzwerk). */
const PAD2_RADIO_CELL = {
  cables: [
    { type: 'LCF78-50JFNA (Coaxial 7/8")', brand: "RFS", meters: 540 },
    { type: 'LCF12-50JFN (Coaxial 1/2")', brand: "RFS", meters: 320 },
    { type: "Ecoflex 10 Plus HEATEX", brand: "SSB Electronic", meters: 180 },
    { type: "H-155 SMA Male → SMA Female (50 cm)", brand: "Belden", meters: 28 },
    { type: "Grounding Cable H07V-K 1G16", brand: "Lapp Kabel", meters: 95 },
  ],
  splitters: [
    { type: "PRO-RPS-N (Splitter)", brand: "Procom", quantity: 4 },
    { type: "PRO-BBPHY 2/70-20-3 dB", brand: "Procom", quantity: 4 },
    { type: "Splitter 3-Way", brand: "Procom", quantity: 6 },
    { type: "Splitter 4-Way", brand: "Procom", quantity: 5 },
  ],
  taps: [
    { type: "PRO-TAP 150-2700-4.8 dB", brand: "Procom", quantity: 8 },
    { type: "PRO-TAP 150-2700-6 dB", brand: "Procom", quantity: 6 },
    { type: "PRO-TAP 150-2700-8 dB", brand: "Procom", quantity: 9 },
    { type: "PRO-TAP 150-2700-10 dB", brand: "Procom", quantity: 5 },
    { type: "PRO-TAP 150-2700-15 dB", brand: "Procom", quantity: 6 },
  ],
  coupling: {
    type: "Coupling Network DEMARC · 4× SLR5500 @ 36 dBm",
    brand: "Procom",
    note: "Hot-redundant gekoppelt — gleichzeitige Nutzung aller 4 Repeater auf gemeinsamem Antennen-Netz",
  },
  overvoltage: { type: "Overvoltage Protection", brand: "Procom", quantity: 8 },
  tray: { type: "Cable Tray (Rejiband 300 mm)", brand: "Pemsa", meters: 720 },
} as const;

/** Netzwerk-Konfig für alle 4 Repeater (Gateway + Subnetz). */
const PAD2_NETWORK = {
  gateway_ip: "192.168.0.1",
  gateway_netmask: "255.255.255.0",
  rest_channel_udp_port: 55001,
} as const;

/** Geräte-Anleitungen (PDFs). */
const PAD2_MANUALS = [
  { model: "Motorola R7", files: [
    { name: "R7_User_Guide_DE.pdf", size_kb: 4200 },
    { name: "R7_Datasheet_EN.pdf", size_kb: 850 },
    { name: "R7_Programming_Manual.pdf", size_kb: 6800 },
  ] },
  { model: "Motorola DP4800e", files: [
    { name: "DP4800e_User_Guide_DE.pdf", size_kb: 3800 },
    { name: "DP4800e_Datasheet_EN.pdf", size_kb: 720 },
    { name: "DP4800e_Service_Manual.pdf", size_kb: 9100 },
  ] },
  { model: "Motorola DM4600", files: [
    { name: "DM4600_Installation_Guide.pdf", size_kb: 5100 },
    { name: "DM4600_Datasheet_EN.pdf", size_kb: 680 },
  ] },
  { model: "Motorola SLR5500", files: [
    { name: "SLR5500_Installation_Manual.pdf", size_kb: 8200 },
    { name: "SLR5500_Service_Manual.pdf", size_kb: 12400 },
    { name: "SLR5500_CPS_Programming_Guide.pdf", size_kb: 3400 },
  ] },
] as const;

/** Aktuelle Tech-Debt-Einträge für PAD2. */
const PAD2_TECH_DEBT = [
  {
    title: "USV-Akku-Gesundheit auf 57 %",
    description:
      "Akku der USV unterschreitet den Schwellwert (Soll: ≥ 75 %). Aktuelle Notstrom-Reserve ca. 18 Minuten — Soll 30 Minuten. Austausch durch Spreenauten-Service vorbereitet.",
    status: "Wartung geplant",
    severity: "warn" as const,
    eta: "2026-06-30",
  },
  {
    title: "Repeater PAD2-REP-03 offline",
    description:
      "Peer 3 (Verteiler P3 Nord) seit 18.05. nicht erreichbar — PA-Temperatur-Excursion. IPSC-Cluster läuft mit 3 Repeatern weiter, Coverage P3 leicht reduziert. Austausch-Repeater unterwegs.",
    status: "Austausch in Bearbeitung",
    severity: "err" as const,
    eta: "2026-05-28",
  },
  {
    title: "Funkzellen-Auslastung im Mittel bei 82 %",
    description:
      "Slot-Utilization über die letzten 12 Wochen durchschnittlich 82 % — kritischer Schwellwert liegt bei 90 %. Erweiterung der Funkzelle um einen 5. SLR5500-Repeater (+25 % Slot-Kapazität) ist geplant.",
    status: "Erweiterung geplant",
    severity: "info" as const,
    eta: "2028-06-15",
  },
] as const;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/** Spreenauten POC für PAD2 — Daniel-spezifische Mock-Daten. */
const PAD2_SPREENAUTEN_POC = {
  name: "Daniel Knappe",
  role: "COT",
  email: "dk@spreenauten.com",
  slack_url: "https://spreenauten.slack.com/team/U09A7071R",
} as const;

/**
 * Amazon POC für PAD2 — Demo-anonymisiert.
 * Echter Kontakt ist Daniel intern bekannt; in der öffentlichen Preview
 * wird nur Vorname + Nachname-Initiale und eine maskierte Email gezeigt.
 * Amazon arbeitet ausschließlich mit Slack — Slack-Link als Platzhalter,
 * echte URL wird vor Live-Gang nachgereicht.
 */
const PAD2_AMAZON_POC = {
  name: "George C.",
  role: "IT Support Engineer",
  email_display: "g****@amazon.com",
  slack_url: "https://amazon.enterprise.slack.com/team/U_GEORGE_PLACEHOLDER",
} as const;

/** Funkgeräte-Inventory für RMA-Suche (Mock — in Production aus dossier_assets). */
const PAD2_RADIO_INVENTORY = [
  // R7 — Garantie 8 Jahre, Start 2025/26 (alle aktiv)
  { radio_id: "1108", serial: "478MNO1234", model: "Motorola R7", purchased_at: "2025-08-20",
    warranty: "AMZ Enterprise Service R7 (inkl. Selbstverschulden)", warranty_until: "2033-08-20" },
  { radio_id: "1145", serial: "478MNO5689", model: "Motorola R7", purchased_at: "2026-01-15",
    warranty: "AMZ Enterprise Service R7 (inkl. Selbstverschulden)", warranty_until: "2034-01-15" },
  { radio_id: "1167", serial: "478MNO7892", model: "Motorola R7", purchased_at: "2025-11-04",
    warranty: "AMZ Enterprise Service R7 (inkl. Selbstverschulden)", warranty_until: "2033-11-04" },
  { radio_id: "1189", serial: "478MNO9012", model: "Motorola R7", purchased_at: "2026-02-28",
    warranty: "AMZ Enterprise Service R7 (inkl. Selbstverschulden)", warranty_until: "2034-02-28" },
  // DP4800e — Garantie 8 Jahre, in der Demo nur aktive (ab 2022)
  { radio_id: "1023", serial: "478JKL0023", model: "Motorola DP4800e", purchased_at: "2022-03-15",
    warranty: "AMZ Enterprise Service DP4800e (ohne Selbstverschulden)", warranty_until: "2030-03-15" },
  { radio_id: "1042", serial: "478JKL0042", model: "Motorola DP4800e", purchased_at: "2022-06-10",
    warranty: "AMZ Enterprise Service DP4800e (ohne Selbstverschulden)", warranty_until: "2030-06-10" },
  { radio_id: "1078", serial: "478JKL0078", model: "Motorola DP4800e", purchased_at: "2023-04-22",
    warranty: "AMZ Enterprise Service DP4800e (ohne Selbstverschulden)", warranty_until: "2031-04-22" },
  { radio_id: "1091", serial: "478JKL0091", model: "Motorola DP4800e", purchased_at: "2023-09-08",
    warranty: "AMZ Enterprise Service DP4800e (ohne Selbstverschulden)", warranty_until: "2031-09-08" },
] as const;

type Radio_ = typeof PAD2_RADIO_INVENTORY[number];

const RMA_FAULT_CATEGORIES = [
  "Display defekt",
  "Akku lädt nicht / hält Ladung nicht",
  "PTT-Taste klemmt / reagiert nicht",
  "Mikrofon defekt",
  "Lautsprecher defekt / leise",
  "Wasserschaden",
  "Sturzschaden / Gehäuse beschädigt",
  "Lädt nicht im IMPRES-Charger",
  "Antenne lose / abgebrochen",
  "Anderes (in Beschreibung)",
] as const;

export default function SiteDetailV213() {
  const { code } = useParams<{ code: string }>();
  const [active, setActive] = useState<TabId>("overview");
  const isPad2 = (code ?? "PAD2").toUpperCase() === "PAD2";

  if (!isPad2) {
    return (
      <ShellV2 back={{ to: "/preview/landing-v2", label: "Zur Übersicht" }}>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <h1 className="text-2xl font-bold tracking-tight">Detail-Seite „{code}" noch nicht verfügbar</h1>
          <p className="mt-2 text-base">
            Im Design-Preview ist nur <strong>PAD2</strong> hinterlegt. Springe zu{" "}
            <a className="font-semibold underline underline-offset-4" href="/preview/sites/PAD2/v2-13">PAD2</a>.
          </p>
        </div>
      </ShellV2>
    );
  }

  const site = PAD2;
  const lastVisit = site.crew_log.at(-1) ?? site.crew_log[0];

  return (
    <ShellV2 kicker={site.code} back={{ to: "/preview/landing-v2", label: "Zur Übersicht" }}>
      <HeroCard site={site} lastVisit={lastVisit} />

      {/* Tab-Bar */}
      <nav className="mt-8 border-b border-slate-200 dark:border-slate-800">
        <div className="-mb-px flex flex-wrap gap-x-2 sm:gap-x-6 overflow-x-auto">
          {TABS.map((t) => {
            const isActive = active === t.id;
            return (
              <button key={t.id} type="button" onClick={() => setActive(t.id)}
                className={`shrink-0 border-b-2 px-1 pb-3 pt-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "border-sky-600 text-slate-900 dark:border-sky-400 dark:text-white"
                    : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
                }`}>
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Tab content */}
      <div className="mt-8">
        {active === "overview" && <OverviewTab onNavigate={setActive} />}
        {active === "devices" && <DevicesTab />}
        {active === "cell" && <CellTab />}
        {active === "freq" && <FreqTab />}
        {active === "codeplugs" && <CodeplugsTab />}
        {active === "security" && <SecurityTab />}
        {active === "rma" && <RmaTab />}
        {active === "techdebt" && <TechDebtTab />}
        {active === "docs" && <DocsTab />}
      </div>
    </ShellV2>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function HeroCard({
  site, lastVisit,
}: Readonly<{ site: typeof PAD2; lastVisit: typeof PAD2.crew_log[number] }>) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex items-start gap-5">
          <img src="/amzel-logo.svg" alt="" aria-hidden className="size-16 shrink-0 rounded-2xl" />
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{site.code}</h1>
            <p className="text-lg text-slate-700 dark:text-slate-200">{site.name.replace(`${site.code} — `, "")}</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                <CircleDot className="size-3" aria-hidden /> Aktiv im Betrieb
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {SITE_TYPE_LABELS[site.business_unit] ?? site.business_unit}
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {site.country}
              </span>
            </div>
            <p className="flex items-start gap-2 pt-1 text-sm text-slate-600 dark:text-slate-400">
              <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden /><span>{site.address}</span>
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 rounded-2xl bg-slate-50 p-5 text-sm sm:grid-cols-3 lg:min-w-[480px] dark:bg-slate-800/60">
          <SpreenautenPocBlock />
          <AmazonPocBlock />
          <ContactRow label="Letzter Besuch" value={`Oliver Hertkorn (CEO) · ${formatDate("2025-12-28")}`} />
        </div>
      </div>
    </section>
  );
}

function ContactRow({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex flex-col">
      <span className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{value}</span>
    </div>
  );
}

function SpreenautenPocBlock() {
  const p = PAD2_SPREENAUTEN_POC;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
        Spreenauten POC
      </span>
      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        {p.name} <span className="font-normal text-slate-500 dark:text-slate-400">({p.role})</span>
      </span>
      <a
        href={p.slack_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-sky-700 hover:underline dark:text-sky-400"
      >
        <MessageSquare className="size-3.5" aria-hidden />
        Slack-Profil
      </a>
      <a
        href={`mailto:${p.email}`}
        className="inline-flex items-center gap-1.5 text-xs text-slate-700 hover:underline dark:text-slate-300"
      >
        <Mail className="size-3.5" aria-hidden />
        {p.email}
      </a>
    </div>
  );
}

function AmazonPocBlock() {
  const p = PAD2_AMAZON_POC;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
        Amazon POC
      </span>
      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        {p.name} <span className="font-normal text-slate-500 dark:text-slate-400">({p.role})</span>
      </span>
      <a
        href={p.slack_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-sky-700 hover:underline dark:text-sky-400"
      >
        <MessageSquare className="size-3.5" aria-hidden />
        Slack-Profil
      </a>
      <span
        className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
        title="Demo-Maskierung — vollständige Adresse intern bekannt"
      >
        <Mail className="size-3.5" aria-hidden />
        {p.email_display}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Übersicht
// ---------------------------------------------------------------------------

function OverviewTab({ onNavigate }: Readonly<{ onNavigate: (t: TabId) => void }>) {
  const site = PAD2;
  const onlineRepeaters = site.repeaters.filter((r) => r.online).length;
  const offlineRepeaters = site.repeaters.length - onlineRepeaters;
  const earliestFreq = [...site.frequencies].sort((a, b) => a.valid_until.localeCompare(b.valid_until))[0];
  const totalDevices = PAD2_DEVICES.reduce((a, d) => a + d.total, 0);
  const inServiceDevices = PAD2_DEVICES.reduce((a, d) => a + d.in_service, 0);
  const inRmaDevices = PAD2_DEVICES.reduce((a, d) => a + d.in_rma, 0);
  const expiresInDays = daysUntil(earliestFreq.valid_until);

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <BigFact onClick={() => onNavigate("devices")}
          icon={<Smartphone className="size-5" aria-hidden />} label="Funkgeräte"
          primary={`${totalDevices}`} secondary={`${inServiceDevices} aktiv im Einsatz`} />
        <BigFact onClick={() => onNavigate("devices")}
          icon={<Battery className="size-5" aria-hidden />} label="Akku-Gesundheit"
          primary={`Ø ${PAD2_BATTERIES.avg_health_pct} %`}
          secondary={`${PAD2_BATTERIES.total} Akkus · ${PAD2_BATTERIES.health_min}–${PAD2_BATTERIES.health_max} %`}
          tone={PAD2_BATTERIES.avg_health_pct < 75 ? "warn" : "ok"} />
        <BigFact onClick={() => onNavigate("devices")}
          icon={<RadioTower className="size-5" aria-hidden />} label="Repeater"
          primary={`${site.repeaters.length}× SLR5500`}
          secondary={offlineRepeaters > 0 ? `${onlineRepeaters} online · ${offlineRepeaters} offline` : `${onlineRepeaters} online`}
          tone={offlineRepeaters > 0 ? "warn" : "ok"} />
        <BigFact onClick={() => onNavigate("cell")}
          icon={<Antenna className="size-5" aria-hidden />} label="Antennen"
          primary={`${site.antennas.total}`} secondary={`${site.antennas.indoor} indoor · ${site.antennas.outdoor} outdoor`} />
        <BigFact onClick={() => onNavigate("freq")}
          icon={<CalendarClock className="size-5" aria-hidden />} label="Frequenz-Lizenz"
          primary={`bis ${formatDate(earliestFreq.valid_until)}`}
          secondary={expiresInDays > 0 ? `noch ${Math.round(expiresInDays / 30)} Monate` : "abgelaufen!"}
          tone={expiresInDays < 90 ? "warn" : "ok"} />
        <BigFact onClick={() => onNavigate("devices")}
          icon={<BatteryCharging className="size-5" aria-hidden />} label="Ladestationen"
          primary={`${PAD2_BATTERIES.chargers}`} secondary={PAD2_BATTERIES.charger_model} />
        <BigFact onClick={() => onNavigate("rma")}
          icon={<Wrench className="size-5" aria-hidden />} label="In Reparatur"
          primary={`${PAD2_RMA.length} RMAs`} secondary={`${inRmaDevices} aus PAD2-Bestand`}
          tone={PAD2_RMA.length > 0 ? "warn" : "ok"} />
        <BigFact onClick={() => onNavigate("security")}
          icon={<ShieldCheck className="size-5" aria-hidden />} label="Verschlüsselung"
          primary="AES-256" secondary={`Rotation: ${formatDate(PAD2_SECRETS[0].rotation_due)}`} />
      </section>

      <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200">
        <p className="flex items-start gap-2">
          <CircleDot className="mt-0.5 size-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
          <span>
            Die Werte oben sind <strong>Durchschnittswerte über alle aktiven Geräte</strong> dieser Site —
            zuletzt aktualisiert am {formatDate(site.last_survey_at)}. Echtzeit-Werte (Akku pro Gerät,
            laufende Calls, Repeater-Auslastung) im
          </span>
        </p>
        <a href="https://dispatcher.amzel.io/sites/PAD2" target="_blank" rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-700 sm:self-auto dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">
          <Radio className="size-4" aria-hidden /> Live im Dispatcher Tool
        </a>
      </section>

      <CpsDownloadCard />
      <SnapshotDownloadCard siteCode={PAD2.code} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Funkgeräte
// ---------------------------------------------------------------------------

function DevicesTab() {
  const site = PAD2;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Funkgeräte-Bestand" icon={<Smartphone className="size-5" aria-hidden />}
        subtitle={`Durchschnitt über aktive Geräte · ${PAD2_DEVICES.reduce((a, d) => a + d.total, 0)} Geräte gesamt`}>
        <ul className="space-y-3">
          {PAD2_DEVICES.map((d) => (
            <li key={d.model} className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <d.icon className="size-4 text-slate-500" aria-hidden />
                  <span className="font-semibold">{d.model}</span>
                </div>
                <span className="text-2xl font-bold tracking-tight">{d.total}</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                <DeviceStat label="Im Einsatz" value={d.in_service} tone="ok" />
                <DeviceStat label="In RMA" value={d.in_rma} tone={d.in_rma > 0 ? "warn" : undefined} />
                <DeviceStat label="Verloren" value={d.missing} tone={d.missing > 0 ? "err" : undefined} />
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Akkus & Ladestationen" icon={<Battery className="size-5" aria-hidden />}
        subtitle={`${PAD2_BATTERIES.total} Akkus · ${PAD2_BATTERIES.chargers} Ladestationen`}>
        <div className="grid grid-cols-2 gap-3">
          <Tile title="Bestand" value={`${PAD2_BATTERIES.total}`} foot="Akkus insgesamt" />
          <Tile title="Ø Gesundheit" value={`${PAD2_BATTERIES.avg_health_pct} %`}
            foot={`Range ${PAD2_BATTERIES.health_min}–${PAD2_BATTERIES.health_max} %`}
            tone={PAD2_BATTERIES.avg_health_pct >= 80 ? "ok" : "warn"} />
        </div>
        <div className="mt-3 flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/30">
          <BatteryCharging className="size-8 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
          <div>
            <div className="font-semibold">{PAD2_BATTERIES.chargers} Ladestationen vor Ort</div>
            <div className="text-sm text-slate-600 dark:text-slate-300">{PAD2_BATTERIES.charger_model}</div>
          </div>
        </div>
      </Card>

      <Card title="Repeater (Status)" icon={<RadioTower className="size-5" aria-hidden />} className="lg:col-span-2"
        subtitle={`${site.repeaters.length}× SLR5500 · Master + 3 Peers · Standort: MDF PAD2 · IT-Raum`}>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {site.repeaters.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{r.id}</span>
                  {r.role === "master" && (
                    <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white dark:bg-white dark:text-slate-900">
                      Master
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400">{r.model} · Firmware {r.firmware}</div>
              </div>
              {r.online ? (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="size-4" aria-hidden /> Online
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-700 dark:text-rose-400">
                  <AlertTriangle className="size-4" aria-hidden /> Offline
                </span>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Repeater-Konfiguration" icon={<RadioTower className="size-5" aria-hidden />} className="lg:col-span-2"
        subtitle={`As-Built · Gateway ${PAD2_NETWORK.gateway_ip} / ${PAD2_NETWORK.gateway_netmask} · REST UDP ${PAD2_NETWORK.rest_channel_udp_port}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-slate-500 dark:border-slate-700 dark:text-slate-400">
                <th className="py-2 pr-3 sticky left-0 bg-white dark:bg-slate-900">Parameter</th>
                {site.repeaters.map((r) => (
                  <th key={r.id} className="py-2 pr-3 whitespace-nowrap">
                    {r.role === "master" ? "Master" : `Peer ${String(site.repeaters.indexOf(r)).padStart(2, "0")}`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              <ConfigRow label="Model" cells={site.repeaters.map((r) => r.model)} />
              <ConfigRow label="Model-Nr" cells={site.repeaters.map((r) => r.model_number)} mono />
              <ConfigRow label="Serial Number" cells={site.repeaters.map((r) => r.serial)} mono />
              <ConfigRow label="Firmware" cells={site.repeaters.map((r) => r.firmware)} mono />
              <ConfigRow label="License" cells={site.repeaters.map((r) => r.license)} />
              <ConfigRow label="Ethernet IP" cells={site.repeaters.map((r) => r.ip)} mono />
              <ConfigRow label="UDP Port" cells={site.repeaters.map((r) => `${r.udp_port}`)} mono />
              <ConfigRow label="Radio Name" cells={site.repeaters.map((r) => r.radio_name)} />
              <ConfigRow label="Radio ID" cells={site.repeaters.map((r) => `${r.radio_id}`)} mono />
              <ConfigRow label="Rx (MHz)" cells={site.repeaters.map((r) => r.rx_mhz.toFixed(4))} mono />
              <ConfigRow label="Tx (MHz)" cells={site.repeaters.map((r) => r.tx_mhz.toFixed(4))} mono />
              <ConfigRow label="System Code" cells={site.repeaters.map((r) => `${r.system_code}`)} mono />
              <ConfigRow label="Slot Channel IDs" cells={site.repeaters.map((r) => r.slot_channel_ids.join(" & "))} mono />
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ConfigRow({ label, cells, mono = false }: Readonly<{ label: string; cells: ReadonlyArray<string>; mono?: boolean }>) {
  return (
    <tr>
      <td className="py-2 pr-3 sticky left-0 bg-white text-[10px] uppercase tracking-widest text-slate-500 dark:bg-slate-900 dark:text-slate-400">
        {label}
      </td>
      {cells.map((c, i) => (
        <td key={`${label}-${i}`} className={`py-2 pr-3 ${mono ? "font-mono" : ""}`}>
          {c}
        </td>
      ))}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Tab: Funkzelle (Antennen + Kabel + Splitter + Taps + Coupling + Heatmaps)
// ---------------------------------------------------------------------------

function CellTab() {
  const site = PAD2;
  const heatmapDocs = site.documents.filter((d) => d.type === "heatmap_pdf");
  const totalCableM = PAD2_RADIO_CELL.cables.reduce((a, c) => a + c.meters, 0);
  const totalSplitters = PAD2_RADIO_CELL.splitters.reduce((a, s) => a + s.quantity, 0);
  const totalTaps = PAD2_RADIO_CELL.taps.reduce((a, t) => a + t.quantity, 0);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Antennen" icon={<Antenna className="size-5" aria-hidden />}
        subtitle={`${site.antennas.total} Antennen verbaut · STS460 DMR + PCPI`}>
        <div className="grid grid-cols-3 gap-3">
          <Tile title="Indoor" value={`${site.antennas.indoor}`} foot="P1–P4" />
          <Tile title="Outdoor" value={`${site.antennas.outdoor}`} foot="Rooftop + Perimeter" />
          <Tile title="Gesamt" value={`${site.antennas.total}`} foot="Mix Sektor + Rundstrahler" />
        </div>
      </Card>

      <Card title="Koppelnetzwerk" icon={<RadioTower className="size-5" aria-hidden />}
        subtitle={`${PAD2_RADIO_CELL.coupling.brand} · 4-fach Hot-Redundant`}>
        <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/30">
          <div className="font-semibold">{PAD2_RADIO_CELL.coupling.type}</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Hersteller: {PAD2_RADIO_CELL.coupling.brand}
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {PAD2_RADIO_CELL.coupling.note}
          </p>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-800/40 dark:text-slate-300">
          <ShieldCheck className="size-4 shrink-0" aria-hidden />
          <span>
            {PAD2_RADIO_CELL.overvoltage.quantity}× {PAD2_RADIO_CELL.overvoltage.type} (
            {PAD2_RADIO_CELL.overvoltage.brand}) — Blitzschutz-Ableiter zwischen Antenne und Koax.
          </span>
        </div>
      </Card>

      <Card title="Antennenkabel" icon={<FileText className="size-5" aria-hidden />} className="lg:col-span-2"
        subtitle={`${PAD2_RADIO_CELL.cables.length} Kabeltypen · gesamt ${totalCableM} m verlegt`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-500 dark:border-slate-700 dark:text-slate-400">
                <th className="py-2 pr-3">Kabel-Typ</th>
                <th className="py-2 pr-3">Marke</th>
                <th className="py-2 pr-3 text-right">Verlegt (m)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {PAD2_RADIO_CELL.cables.map((c) => (
                <tr key={c.type}>
                  <td className="py-2 pr-3 font-mono text-xs">{c.type}</td>
                  <td className="py-2 pr-3 text-slate-600 dark:text-slate-300">{c.brand}</td>
                  <td className="py-2 pr-3 text-right font-semibold">{c.meters}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-2 pr-3 text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Gesamt</td>
                <td className="py-2 pr-3">{PAD2_RADIO_CELL.tray.type} · {PAD2_RADIO_CELL.tray.meters} m</td>
                <td className="py-2 pr-3 text-right">{totalCableM} m</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Splitter" icon={<RadioTower className="size-5" aria-hidden />}
        subtitle={`${totalSplitters} Stück · ${PAD2_RADIO_CELL.splitters.length} Typen`}>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {PAD2_RADIO_CELL.splitters.map((s) => (
            <li key={s.type} className="flex items-center justify-between py-2.5">
              <div>
                <div className="text-sm font-medium">{s.type}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{s.brand}</div>
              </div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {s.quantity}× verbaut
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Taps (Dämpfungsglieder)" icon={<RadioTower className="size-5" aria-hidden />}
        subtitle={`${totalTaps} Stück · gestaffelte Dämpfung 4.8–15 dB`}>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {PAD2_RADIO_CELL.taps.map((t) => (
            <li key={t.type} className="flex items-center justify-between py-2.5">
              <div>
                <div className="text-sm font-medium">{t.type}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{t.brand}</div>
              </div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {t.quantity}× verbaut
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Coverage-Heatmaps" icon={<MapPin className="size-5" aria-hidden />} className="lg:col-span-2"
        subtitle={`${heatmapDocs.length} Karten · Letzte Survey ${formatDate(site.last_survey_at)}`}>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {heatmapDocs.map((d) => (
            <li key={d.name}
              className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-sm dark:border-slate-800 dark:bg-slate-800/30">
              <FileText className="size-4 text-slate-400" aria-hidden />
              <span className="min-w-0 flex-1 truncate" title={d.name}>{d.name}</span>
              <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{fmtKb(d.size_kb)}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Frequenzen
// ---------------------------------------------------------------------------

function FreqTab() {
  const site = PAD2;
  const earliest = [...site.frequencies].sort((a, b) => a.valid_until.localeCompare(b.valid_until))[0];
  const total = site.frequencies.reduce((a, f) => a + f.annual_cost_eur, 0);
  return (
    <Card title="Frequenzzuteilung" icon={<CalendarClock className="size-5" aria-hidden />}
      subtitle={`${site.frequencies.length} Kanäle · Behörde ${earliest.authority}`}>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {site.frequencies.map((f) => (
          <li key={f.id} className="flex items-center justify-between py-3">
            <div>
              <div className="font-mono text-sm font-semibold">{f.rx.toFixed(4)} / {f.tx.toFixed(4)} MHz</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{f.ref} · {f.bandwidth_khz} kHz</div>
            </div>
            <div className="text-right text-sm">
              <div className="font-medium text-slate-700 dark:text-slate-200">bis {formatDate(f.valid_until)}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Kosten: <CostBearerTooltip>Amazon</CostBearerTooltip> · {f.annual_cost_eur} €/Jahr
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/40">
        <Stat label="Behörde" value={earliest.authority} />
        <div className="text-center">
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
            Kostenträger
          </div>
          <div className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-100">
            <CostBearerTooltip>Amazon</CostBearerTooltip>
          </div>
        </div>
        <Stat label="Gesamt p.a." value={`${total} €`} />
      </div>
    </Card>
  );
}

/** Tooltip-Wrapper für "Amazon"-Kostenträger mit Daniel-spezifischem Hint. */
function CostBearerTooltip({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <span className="group relative inline-flex cursor-help items-center underline decoration-dotted decoration-slate-400 underline-offset-2">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none invisible absolute bottom-full left-1/2 z-30 mb-2 w-72 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-normal text-slate-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 dark:bg-slate-100 dark:text-slate-900"
      >
        Amazon zahlt in PAD2 die Frequenzen selbst. In anderen Sites wird das von der
        Spreenauten GmbH übernommen und AMZ 1:1 in Rechnung gestellt — Abrechnung auf Basis
        einer PO.
        <span className="absolute left-1/2 top-full size-0 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent border-t-slate-900 dark:border-t-slate-100" aria-hidden />
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tab: Codeplugs
// ---------------------------------------------------------------------------

function CodeplugsTab() {
  return (
    <Card title="Codeplugs pro Modell + Department" icon={<ShieldCheck className="size-5" aria-hidden />}
      subtitle={`${PAD2_CODEPLUGS_BY_MODEL.length} Geräte-Modelle · Site-Standard + Department-Varianten`}>
      <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-sm dark:border-slate-800 dark:bg-slate-800/30">
        <div className="flex items-center gap-2">
          <Download className="size-4 text-slate-500 dark:text-slate-400" aria-hidden />
          <div>
            <div className="font-semibold">Motorola CPS Software v{PAD2_CPS.current.version}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Aktuelle Version · {PAD2_CPS.current.platform} · plus {PAD2_CPS.legacy.length} Legacy-Versionen
            </div>
          </div>
        </div>
        <button type="button"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">
          <Download className="size-3.5" aria-hidden /> Download
        </button>
      </div>
      <ul className="space-y-4">
        {PAD2_CODEPLUGS_BY_MODEL.map((g) => (
          <li key={g.model}>
            <div className="mb-2">
              <div className="font-semibold">{g.model}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{g.subtitle}</div>
            </div>
            <ul className="space-y-2">
              {g.versions.map((v) => (
                <li key={`${g.model}-${v.version}`}
                  className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold">{v.version}</span>
                      <DepartmentBadge name={v.department} />
                    </div>
                    <span className="text-xs text-slate-500 dark:text-slate-400">ausgerollt am {formatDate(v.deployed_at)}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{v.note}</p>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tab: Sicherheit
// ---------------------------------------------------------------------------

function SecurityTab() {
  return (
    <Card title="Verschlüsselung & Zugangsdaten" icon={<Lock className="size-5" aria-hidden />}
      subtitle={`${PAD2_SECRETS.length} Secrets · AES-256 + AWS KMS envelope-encrypted`}>
      <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm dark:border-amber-500/30 dark:bg-amber-500/10">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
        <div>
          <p className="font-semibold text-amber-900 dark:text-amber-200">
            Hoch verschlüsselt gespeichert · Reveal nur für befugte Personen
          </p>
          <p className="mt-1 text-amber-800 dark:text-amber-300">
            Alle Schlüssel und Zugangsdaten liegen <strong>envelope-encrypted</strong> in AWS KMS
            (Customer-Managed-Key, EU-Region). Zusätzlich werden sie mit einer{" "}
            <strong>Post-Quantum-Schicht (ML-KEM / CRYSTALS-Kyber)</strong> gehärtet — gegen
            zukünftige Quantum-Computer-Angriffe. Eine Anzeige im Klartext löst immer ein
            Audit-Event, eine <strong>Slack-Benachrichtigung</strong> und einen 30-Sekunden-Countdown
            bis zum automatischen Re-Hide aus. Rotation alle 90 Tage.
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {PAD2_SECRETS.map((s) => {
          const overdue = daysUntil(s.rotation_due) < 0;
          return (
            <li key={s.label}
              className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  {s.kind === "funk"
                    ? <KeyRound className="size-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                    : <Lock className="size-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />}
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{s.label}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{s.sub}</div>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-mono text-xs text-slate-400 dark:text-slate-500">••••••••••</div>
                  <button type="button"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:underline dark:text-amber-300">
                    <Eye className="size-3" aria-hidden /> Reveal anfordern
                  </button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                <span>Zuletzt rotiert: {formatDate(s.last_rotated)}</span>
                <span className={overdue ? "text-rose-700 dark:text-rose-400 font-medium" : ""}>
                  Nächste Rotation: {formatDate(s.rotation_due)} {overdue && "(überfällig)"}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tab: RMA
// ---------------------------------------------------------------------------

function RmaTab() {
  const [wizardOpen, setWizardOpen] = useState(false);

  return (
    <Card title="RMA / Reparaturen" icon={<Wrench className="size-5" aria-hidden />}
      subtitle={`${PAD2_RMA.length} Geräte aktuell in Reparatur`}>
      {!wizardOpen && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30">
          <div className="flex items-center gap-2 text-sm">
            <PackageOpen className="size-5 text-slate-500 dark:text-slate-400" aria-hidden />
            <span>Defektes Gerät erfassen und RMA anlegen</span>
          </div>
          <button type="button" onClick={() => setWizardOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">
            <Wrench className="size-4" aria-hidden /> RMA anmelden
          </button>
        </div>
      )}

      {wizardOpen && <RmaWizard onClose={() => setWizardOpen(false)} />}

      <h3 className="mb-2 text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        Laufende RMAs
      </h3>
      <ul className="space-y-2">
        {PAD2_RMA.map((r) => {
          const eta = daysUntil(r.eta);
          let etaText: string;
          if (eta > 0) etaText = `in ${eta} Tagen`;
          else if (eta === 0) etaText = "heute";
          else etaText = `${-eta} Tage überfällig`;
          return (
            <li key={r.rma_id}
              className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/30">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">{r.rma_id}</span>
                    <span className="font-semibold">{r.model}</span>
                  </div>
                  <div className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{r.issue}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-xs text-slate-500 dark:text-slate-400">
                    <span>SN: {r.serial}</span>
                    <span>Radio-ID: {r.radio_id}</span>
                    <span>Versand: {formatDate(r.sent_at)}</span>
                    <span>Werkstatt: {r.vendor}</span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs text-slate-500 dark:text-slate-400">Rückkehr erwartet</div>
                  <div className={`font-semibold ${eta < 7 ? "text-emerald-700 dark:text-emerald-400" : "text-slate-700 dark:text-slate-200"}`}>
                    {formatDate(r.eta)}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{etaText}</div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-800/40 dark:text-slate-300">
        <div className="font-semibold text-slate-700 dark:text-slate-200">Spreenauten-Werkstätten</div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {SPREENAUTEN_WORKSHOPS.map((w) => (
            <span key={w} className="inline-flex items-center gap-1">
              <Wrench className="size-3" aria-hidden />{w}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// RMA-Wizard — 3-Schritt-Flow zum Anlegen einer neuen RMA
// ---------------------------------------------------------------------------

function RmaWizard({ onClose }: Readonly<{ onClose: () => void }>) {
  const [mode, setMode] = useState<"search" | "qr">("search");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Radio_ | null>(null);
  const [fault, setFault] = useState<string>("");
  const [description, setDescription] = useState("");
  const [workshop, setWorkshop] = useState<string>(SPREENAUTEN_WORKSHOPS[0]);
  const [done, setDone] = useState(false);

  const filtered = !query.trim() ? [] : PAD2_RADIO_INVENTORY.filter((r) => {
    const q = query.toLowerCase();
    return r.radio_id.includes(q) || r.serial.toLowerCase().includes(q) || r.model.toLowerCase().includes(q);
  }).slice(0, 6);

  const canSubmit = selected !== null && fault !== "" && description.trim().length > 5;

  function submit() {
    if (canSubmit) setDone(true);
  }

  if (done && selected) {
    return (
      <div className="mb-4 overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10">
        <div className="flex items-start gap-3 p-5">
          <CheckCircle className="mt-0.5 size-6 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
          <div className="flex-1">
            <h4 className="text-base font-bold tracking-tight text-emerald-900 dark:text-emerald-100">
              RMA erfasst · neue Vorgangsnummer: <span className="font-mono">RMA-2026-0441</span>
            </h4>
            <p className="mt-1 text-sm text-emerald-900/90 dark:text-emerald-200">
              <strong>{selected.model}</strong> · Radio-ID {selected.radio_id} · SN {selected.serial} —
              wird abgeholt durch <strong>{workshop}</strong>. Garantie-Anspruch geprüft:{" "}
              <strong>{selected.warranty}</strong> (gültig bis {formatDate(selected.warranty_until)}).
              Anforderer + Audit-Notiz wurden erfasst.
            </p>
            <button type="button" onClick={onClose}
              className="mt-3 text-xs font-medium text-emerald-800 underline-offset-2 hover:underline dark:text-emerald-300">
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 overflow-hidden rounded-2xl border border-sky-200 bg-sky-50 dark:border-sky-500/30 dark:bg-sky-500/10">
      <header className="flex items-center justify-between border-b border-sky-200 p-4 dark:border-sky-500/30">
        <div className="flex items-center gap-2">
          <Wrench className="size-5 text-sky-700 dark:text-sky-300" aria-hidden />
          <h4 className="text-base font-bold tracking-tight text-sky-950 dark:text-sky-100">RMA anmelden</h4>
        </div>
        <button type="button" onClick={onClose} aria-label="Schließen"
          className="inline-flex size-8 items-center justify-center rounded-full text-sky-800 hover:bg-sky-100 dark:text-sky-200 dark:hover:bg-sky-500/20">
          <X className="size-4" aria-hidden />
        </button>
      </header>

      <div className="space-y-5 p-5">
        {/* Schritt 1 */}
        <section>
          <StepHeader n={1} title="Funkgerät identifizieren" done={selected !== null} />
          {selected === null && (
            <>
              <div className="mb-3 flex gap-2">
                <ModeButton active={mode === "search"} onClick={() => setMode("search")}
                  icon={<Search className="size-4" aria-hidden />} label="Nach Radio-ID oder SN suchen" />
                <ModeButton active={mode === "qr"} onClick={() => setMode("qr")}
                  icon={<QrCode className="size-4" aria-hidden />} label="QR-Code mit Handy scannen" />
              </div>

              {mode === "search" && (
                <div>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} autoFocus
                      placeholder="z.B. 1108 oder 478JKL0042…"
                      className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-500/30" />
                  </div>
                  {filtered.length > 0 && (
                    <ul className="mt-2 max-h-96 space-y-1 overflow-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                      {filtered.map((r) => {
                        const daysLeft = daysUntil(r.warranty_until);
                        const monthsLeft = Math.round(daysLeft / 30);
                        let warrantyTone: "ok" | "warn" | "err";
                        if (daysLeft > 365) warrantyTone = "ok";
                        else if (daysLeft > 90) warrantyTone = "warn";
                        else warrantyTone = "err";
                        const warrantyCls = warrantyTone === "ok"
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300"
                          : warrantyTone === "warn"
                            ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                            : "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300";
                        return (
                          <li key={r.radio_id}>
                            <button type="button" onClick={() => setSelected(r)}
                              className="flex w-full items-start justify-between gap-3 rounded-lg p-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-baseline gap-x-2">
                                  <span className="font-semibold text-sm">{r.model}</span>
                                  <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                                    {r.radio_id} · {r.serial}
                                  </span>
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${warrantyCls}`}>
                                    <ShieldCheck className="size-3" aria-hidden />
                                    Garantie {monthsLeft >= 12
                                      ? `noch ${Math.floor(monthsLeft / 12)} J ${monthsLeft % 12} M`
                                      : `noch ${monthsLeft} M`}
                                  </span>
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                                    {r.warranty}
                                  </span>
                                </div>
                              </div>
                              <div className="shrink-0 text-right text-[11px] text-slate-500 dark:text-slate-400">
                                Kauf<br />{formatDate(r.purchased_at)}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {query.trim() && filtered.length === 0 && (
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Kein Treffer — Radio-ID muss exakt aus dem PAD2-Bestand stammen.
                    </p>
                  )}
                </div>
              )}

              {mode === "qr" && (
                <div className="flex flex-col items-center gap-4 rounded-xl border border-slate-200 bg-white p-6 text-center dark:border-slate-700 dark:bg-slate-900 sm:flex-row sm:text-left">
                  <div className="flex size-32 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
                    <QrCode className="size-20 text-slate-700 dark:text-slate-200" aria-hidden />
                  </div>
                  <div>
                    <div className="font-semibold">QR-Code mit dem Handy scannen</div>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      Das Handy öffnet die mobile RMA-Form. Dort kannst du den <strong>Strichcode</strong>{" "}
                      des Funkgeräts scannen — die Radio-ID wird automatisch erkannt.
                    </p>
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Im Preview: tippe alternativ auf „Suchen" oben.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {selected && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{selected.model}</div>
                  <div className="mt-1 font-mono text-xs text-slate-500 dark:text-slate-400">
                    Radio-ID {selected.radio_id} · SN {selected.serial}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      Gekauft: {formatDate(selected.purchased_at)}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                      <ShieldCheck className="size-3" aria-hidden />
                      Garantie bis {formatDate(selected.warranty_until)}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">{selected.warranty}</div>
                </div>
                <button type="button" onClick={() => { setSelected(null); setQuery(""); }}
                  className="shrink-0 text-xs font-medium text-slate-600 underline-offset-2 hover:underline dark:text-slate-300">
                  Anderes Gerät
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Schritt 2 */}
        {selected && (
          <section>
            <StepHeader n={2} title="Schaden beschreiben" done={fault !== "" && description.trim().length > 5} />
            <div className="space-y-3">
              <div>
                <label htmlFor="rma-fault" className="mb-1 block text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Fehlerkategorie
                </label>
                <select id="rma-fault" value={fault} onChange={(e) => setFault(e.target.value)}
                  className="w-full rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-sky-400 dark:focus:ring-sky-500/30">
                  <option value="" disabled>— Bitte auswählen —</option>
                  {RMA_FAULT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="rma-desc" className="mb-1 block text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Was ist passiert?
                </label>
                <textarea id="rma-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Kurz beschreiben — was geht nicht, wann zuerst aufgefallen?"
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-500/30" />
              </div>
              <button type="button"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:underline dark:text-sky-300">
                <Camera className="size-3.5" aria-hidden /> Foto vom Defekt anhängen (optional)
              </button>
            </div>
          </section>
        )}

        {/* Schritt 3 */}
        {selected && (
          <section>
            <StepHeader n={3} title="Werkstatt wählen & abschicken" done={false} />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex-1">
                <label htmlFor="rma-workshop" className="mb-1 block text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  Werkstatt
                </label>
                <select id="rma-workshop" value={workshop} onChange={(e) => setWorkshop(e.target.value)}
                  className="w-full rounded-full border border-slate-300 bg-white px-4 py-2.5 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-sky-400 dark:focus:ring-sky-500/30">
                  {SPREENAUTEN_WORKSHOPS.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <button type="button" disabled={!canSubmit} onClick={submit}
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-sky-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-sky-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:bg-sky-500 dark:hover:bg-sky-400 dark:text-sky-950 dark:disabled:bg-slate-700">
                <ArrowRight className="size-4" aria-hidden /> RMA absenden
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function StepHeader({ n, title, done }: Readonly<{ n: number; title: string; done: boolean }>) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className={`inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
        done
          ? "bg-emerald-500 text-white"
          : "bg-sky-600 text-white dark:bg-sky-500 dark:text-sky-950"
      }`}>
        {done ? <CheckCircle2 className="size-4" aria-hidden /> : n}
      </span>
      <h5 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h5>
    </div>
  );
}

function ModeButton({
  active, onClick, icon, label,
}: Readonly<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }>) {
  return (
    <button type="button" onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${
        active
          ? "border-sky-500 bg-white text-sky-900 shadow-sm dark:border-sky-400 dark:bg-slate-900 dark:text-sky-200"
          : "border-slate-200 bg-white/60 text-slate-600 hover:bg-white dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:bg-slate-900"
      }`}>
      {icon}{label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tab: Tech Debt
// ---------------------------------------------------------------------------

const TECH_DEBT_TONE = {
  err: {
    pill: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
    border: "border-rose-200 dark:border-rose-500/30",
    bg: "bg-rose-50/50 dark:bg-rose-500/5",
    icon: "text-rose-600 dark:text-rose-400",
  },
  warn: {
    pill: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
    border: "border-amber-200 dark:border-amber-500/30",
    bg: "bg-amber-50/50 dark:bg-amber-500/5",
    icon: "text-amber-600 dark:text-amber-400",
  },
  info: {
    pill: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
    border: "border-sky-200 dark:border-sky-500/30",
    bg: "bg-sky-50/50 dark:bg-sky-500/5",
    icon: "text-sky-600 dark:text-sky-400",
  },
} as const;

function TechDebtTab() {
  return (
    <Card title="Tech Debt" icon={<AlertTriangle className="size-5" aria-hidden />}
      subtitle={`${PAD2_TECH_DEBT.length} offene Punkte · Status, Severity und geplante Fertigstellung`}>
      <ul className="space-y-3">
        {PAD2_TECH_DEBT.map((d) => {
          const tone = TECH_DEBT_TONE[d.severity];
          const days = daysUntil(d.eta);
          let etaText: string;
          if (days < 0) etaText = `${-days} Tage überfällig`;
          else if (days === 0) etaText = "heute";
          else if (days < 31) etaText = `in ${days} Tagen`;
          else etaText = `in ${Math.round(days / 30)} Monaten`;
          return (
            <li key={d.title}
              className={`rounded-2xl border ${tone.border} ${tone.bg} p-4`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`mt-0.5 size-5 shrink-0 ${tone.icon}`} aria-hidden />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                      {d.title}
                    </h3>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tone.pill}`}>
                      {d.status}
                    </span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                    {d.description}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs text-slate-500 dark:text-slate-400">Fertigstellung</div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {formatDate(d.eta)}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{etaText}</div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
        Tech-Debt-Einträge werden von Spreenauten gepflegt und mit jedem Vor-Ort-Besuch
        aktualisiert. Status- und ETA-Änderungen lösen eine Slack-Notification an den
        Amazon POC aus.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tab: Dokumente & Crew
// ---------------------------------------------------------------------------

function DocsTab() {
  const site = PAD2;
  const dox = {
    "Dokumentation Funkzelle": site.documents.filter((d) => d.type === "install_pdf"),
    Zeichnungen: site.documents.filter((d) => d.type === "autocad_layout"),
    Heatmaps: site.documents.filter((d) => d.type === "heatmap_pdf"),
    Frequenzurkunden: site.documents.filter((d) => d.type === "frequency_certificate"),
  };
  const totalManuals = PAD2_MANUALS.reduce((a, m) => a + m.files.length, 0);

  return (
    <div className="space-y-4">
      <Card title="Crew-History" icon={<Users className="size-5" aria-hidden />}
        subtitle={`${site.crew_log.length} Einsätze dokumentiert`}>
        <ol className="relative ml-3 space-y-4 border-l border-slate-200 pl-5 dark:border-slate-700">
          {[...site.crew_log].reverse().map((v, idx) => (
            <li key={`${v.date}-${v.visitor}-${idx}`} className="relative">
              <span className="absolute -left-6 top-1.5 size-3 rounded-full border-2 border-white bg-slate-300 dark:border-slate-900 dark:bg-slate-600" aria-hidden />
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold">{v.visitor}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {formatDate(v.date)} · {ACTIVITY_LABELS[v.activity] ?? v.activity}
                </span>
              </div>
              <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">{v.summary}</p>
            </li>
          ))}
        </ol>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {Object.entries(dox).map(([category, files]) => (
          <Card key={category} title={category} icon={<FileText className="size-5" aria-hidden />}
            subtitle={`${files.length} ${files.length === 1 ? "Datei" : "Dateien"}`}>
            {files.length === 0 ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">Aktuell keine Dokumente in dieser Kategorie.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {files.map((d) => (
                  <li key={d.name} className="flex items-center justify-between py-2.5">
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="size-4 shrink-0 text-slate-400" aria-hidden />
                      <span className="truncate text-sm" title={d.name}>{d.name}</span>
                    </div>
                    <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{fmtKb(d.size_kb)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}
      </div>

      <Card title="Anleitungen" icon={<FileText className="size-5" aria-hidden />}
        subtitle={`${totalManuals} Hersteller-PDFs für ${PAD2_MANUALS.length} Geräte-Modelle`}>
        <div className="space-y-4">
          {PAD2_MANUALS.map((m) => (
            <div key={m.model}>
              <div className="mb-2 flex items-baseline gap-2">
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{m.model}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {m.files.length} {m.files.length === 1 ? "PDF" : "PDFs"}
                </span>
              </div>
              <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/60 dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-800/30">
                {m.files.map((f) => (
                  <li key={f.name} className="flex items-center justify-between px-3 py-2.5">
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="size-4 shrink-0 text-slate-400" aria-hidden />
                      <span className="truncate text-sm" title={f.name}>{f.name}</span>
                    </div>
                    <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{fmtKb(f.size_kb)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared mini-components
// ---------------------------------------------------------------------------

function Card({
  title, subtitle, icon, children, className = "",
}: Readonly<{ title: string; subtitle?: string; icon: React.ReactNode; children: React.ReactNode; className?: string }>) {
  return (
    <section className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 ${className}`}>
      <header className="mb-4 flex items-center gap-3">
        <span className="inline-flex size-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">{icon}</span>
        <div>
          <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
        </div>
      </header>
      {children}
    </section>
  );
}

const BIG_FACT_TONE = {
  warn: "text-amber-700 dark:text-amber-400",
  ok: "text-emerald-700 dark:text-emerald-400",
  default: "text-slate-900 dark:text-slate-100",
} as const;

function BigFact({ icon, label, primary, secondary, tone, onClick }: Readonly<{
  icon: React.ReactNode; label: string; primary: string; secondary?: string;
  tone?: "ok" | "warn"; onClick?: () => void;
}>) {
  const content = (
    <>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        <span className="text-slate-400 dark:text-slate-500">{icon}</span><span>{label}</span>
      </div>
      <div className={`mt-2 text-xl font-bold tracking-tight ${BIG_FACT_TONE[tone ?? "default"]}`}>{primary}</div>
      {secondary && <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{secondary}</div>}
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick}
        className="group rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:border-sky-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-sky-500/60">
        {content}
      </button>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      {content}
    </div>
  );
}

function Tile({ title, value, foot, tone }: Readonly<{ title: string; value: string; foot?: string; tone?: "ok" | "warn" }>) {
  let t: string;
  if (tone === "ok") t = "text-emerald-700 dark:text-emerald-400";
  else if (tone === "warn") t = "text-amber-700 dark:text-amber-400";
  else t = "text-slate-900 dark:text-slate-100";
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/30">
      <div className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">{title}</div>
      <div className={`mt-1 text-3xl font-bold tracking-tight ${t}`}>{value}</div>
      {foot && <div className="text-sm text-slate-600 dark:text-slate-300">{foot}</div>}
    </div>
  );
}

function Stat({ label, value }: Readonly<{ label: string; value: string | number }>) {
  return (
    <div className="text-center">
      <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

const DEVICE_TONE = {
  ok: "text-emerald-700 dark:text-emerald-400",
  warn: "text-amber-700 dark:text-amber-400",
  err: "text-rose-700 dark:text-rose-400",
  default: "text-slate-900 dark:text-slate-100",
} as const;

function DeviceStat({ label, value, tone }: Readonly<{ label: string; value: number; tone?: "ok" | "warn" | "err" }>) {
  return (
    <div className="rounded-lg bg-white p-2 dark:bg-slate-900">
      <div className={`text-lg font-bold ${DEVICE_TONE[tone ?? "default"]}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}

const DEPT_COLORS: Record<string, string> = {
  "Site-Standard": "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  Operations: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  "IT / Onsite-Tech": "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
};

function DepartmentBadge({ name }: Readonly<{ name: string }>) {
  const cls = DEPT_COLORS[name] ?? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{name}</span>;
}

// ---------------------------------------------------------------------------
// CPS + Snapshot Cards
// ---------------------------------------------------------------------------

function CpsDownloadCard() {
  const [showLegacy, setShowLegacy] = useState(false);
  const current = PAD2_CPS.current;
  return (
    <section className="overflow-hidden rounded-3xl border border-violet-200 bg-violet-50 dark:border-violet-500/30 dark:bg-violet-500/10">
      <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
        <div className="flex items-start gap-3">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-300">
            <PackageOpen className="size-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="text-lg font-bold tracking-tight text-violet-950 dark:text-violet-100">
              Motorola CPS Software · Version {current.version}
            </h3>
            <p className="mt-1 text-sm text-violet-900 dark:text-violet-200">
              Programmier-Software für R7 / DP4800e / DM4600 / SLR5500. Plattform: {current.platform}.
              Veröffentlicht am {formatDate(current.released_at)} · {current.size_mb} MB · Firmware {current.firmware_range}.
            </p>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-violet-800/80 dark:text-violet-300/80">
              <span>Datei: <span className="font-mono">{current.filename}</span></span>
              <a href={current.release_notes_url} target="_blank" rel="noreferrer"
                className="underline underline-offset-2 hover:text-violet-900 dark:hover:text-violet-100">Release-Notes ↗</a>
            </div>
          </div>
        </div>
        <button type="button"
          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-violet-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-violet-800 dark:bg-violet-500 dark:hover:bg-violet-400 dark:text-violet-950">
          <Download className="size-4" aria-hidden /> CPS herunterladen
        </button>
      </div>

      {/* Legacy-CPS-Versionen */}
      <div className="border-t border-violet-200/70 px-6 pb-5 pt-3 sm:px-7 dark:border-violet-500/20">
        <button type="button" onClick={() => setShowLegacy((o) => !o)}
          className="inline-flex items-center gap-2 text-sm font-medium text-violet-800 hover:underline dark:text-violet-200">
          {showLegacy ? "Legacy-Versionen ausblenden" : `${PAD2_CPS.legacy.length} Legacy-Versionen anzeigen`}
          <span aria-hidden className={`text-xs transition-transform ${showLegacy ? "rotate-180" : ""}`}>▼</span>
        </button>
        {!showLegacy && (
          <p className="mt-1 text-xs text-violet-700/80 dark:text-violet-300/80">
            CPS ist nicht abwärtskompatibel — für Funkgeräte mit älterer Firmware wird die passende
            ältere CPS-Version benötigt.
          </p>
        )}
        {showLegacy && (
          <ul className="mt-3 space-y-2">
            {PAD2_CPS.legacy.map((l) => (
              <li key={l.version}
                className="flex flex-col gap-3 rounded-xl border border-violet-200/70 bg-white/60 p-3 sm:flex-row sm:items-center sm:justify-between dark:border-violet-500/20 dark:bg-slate-900/40">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-semibold text-violet-950 dark:text-violet-100">
                      v{l.version}
                    </span>
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-800 dark:bg-violet-500/20 dark:text-violet-200">
                      Firmware {l.firmware_range}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-violet-800/80 dark:text-violet-300/80">{l.note}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-violet-700/70 dark:text-violet-300/60">
                    <span>Released {formatDate(l.released_at)}</span>
                    <span>{l.size_mb} MB</span>
                    <span className="font-mono">{l.filename}</span>
                  </div>
                </div>
                <button type="button"
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-violet-800 dark:bg-violet-500 dark:hover:bg-violet-400 dark:text-violet-950">
                  <Download className="size-3.5" aria-hidden /> Download
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const AMAZON_EMAIL_RE = /^[^\s@]+@amazon\.com$/i;

function SnapshotDownloadCard({ siteCode }: Readonly<{ siteCode: string }>) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const isValid = AMAZON_EMAIL_RE.test(email.trim());

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isValid) setSubmitted(true);
  }

  if (submitted) {
    return (
      <section className="overflow-hidden rounded-3xl border border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10">
        <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
          <div className="flex items-start gap-3">
            <CheckCircle className="mt-0.5 size-6 shrink-0 text-emerald-700 dark:text-emerald-400" aria-hidden />
            <div>
              <h3 className="text-lg font-bold tracking-tight text-emerald-900 dark:text-emerald-100">Snapshot-Anforderung erfasst</h3>
              <p className="mt-1 text-sm text-emerald-900/90 dark:text-emerald-200">
                Verschlüsselte ZIP mit Stand von {siteCode} wird gleich heruntergeladen. Das
                <strong> Entschlüsselungs-Passwort</strong> wurde separat per E-Mail an{" "}
                <strong className="font-mono">{email}</strong> gesendet.
              </p>
            </div>
          </div>
          <button type="button"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 dark:bg-emerald-500 dark:hover:bg-emerald-400 dark:text-emerald-950">
            <Download className="size-4" aria-hidden />{siteCode}-snapshot-2026-05-21.zip
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-3xl border border-sky-200 bg-sky-50 dark:border-sky-500/30 dark:bg-sky-500/10">
      <div className="p-6 sm:p-7">
        <div className="flex items-start gap-3">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-300">
            <FileArchive className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold tracking-tight text-sky-950 dark:text-sky-100">
              Daten-Snapshot für Amazon-Audit
            </h3>
            <p className="mt-1 text-sm text-sky-900 dark:text-sky-200">
              Aktueller Stand von {siteCode} als <strong>verschlüsselte ZIP</strong>. Das Passwort wird
              <strong> separat</strong> an die angegebene <strong className="font-mono">@amazon.com</strong>-Adresse versandt.
            </p>
            <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden />
                <input type="email" required placeholder="vorname.nachname@amazon.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-500/30" />
              </div>
              <button type="submit" disabled={!isValid}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-sky-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:bg-sky-500 dark:hover:bg-sky-400 dark:text-sky-950 dark:disabled:bg-slate-700">
                <Download className="size-4" aria-hidden /> Snapshot anfordern
              </button>
            </form>
            {email && !isValid && (
              <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">
                Nur Adressen auf <strong className="font-mono">@amazon.com</strong> sind berechtigt.
              </p>
            )}
            <ul className="mt-4 grid grid-cols-1 gap-1 text-xs text-sky-900/80 dark:text-sky-200/80 sm:grid-cols-3">
              <li className="flex items-center gap-1.5"><Lock className="size-3.5" aria-hidden /> AES-256 verschlüsselt</li>
              <li className="flex items-center gap-1.5"><Mail className="size-3.5" aria-hidden /> Passwort separat per Mail</li>
              <li className="flex items-center gap-1.5"><ShieldCheck className="size-3.5" aria-hidden /> Audit-Trail pro Anforderung</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}